"use client";

import { SearchResults } from "@/components/InternalSearch/InternalSearchResults";
import {
  getCscSearchResults,
  setQueryParameter,
  updateQueryParameters,
} from "@/helpers";
import {
  Filters,
  GetSearchResultFilteredCategory,
  GetSearchResultProduct,
  TreeNode,
} from "@/types/General";
import { useSearchParams } from "next/navigation";
import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
  useCallback,
  Suspense,
} from "react";

type CscInternalSearchContextType = {
  filters: {
    label: string;
    name: string;
    categories: TreeNode[];
  }[];
  selectedFilters: Filters;
  isLoading: boolean;
  numberOfResults: number;
  searchExpression: string;
  searchResults?: SearchResults;
  bannerInfo: GetSearchResultProduct | null;
  currentPage: number;
  onFilter: (newFilters: Filters) => void;
  onGoToNextPage: () => void;
  onGoToPreviousPage: () => void;
};
export const CscInternalSearchContext =
  createContext<CscInternalSearchContextType>({
    filters: [],
    selectedFilters: [],
    isLoading: false,
    numberOfResults: 0,
    searchExpression: "",
    searchResults: undefined,
    bannerInfo: null,
    currentPage: 1,
    onFilter: () => {},
    onGoToNextPage: () => {},
    onGoToPreviousPage: () => {},
  });

type CscInternalSearchContextBlockProps = {
  sitesFilterText: string;
  categoriesFilterText: string;
};

type CscFiltersType = {
  label: string;
  name: string;
  categories: TreeNode[];
}[];

const CscInternalSearchContextBlockInner: FC<
  PropsWithChildren<CscInternalSearchContextBlockProps>
> = ({ children, categoriesFilterText, sitesFilterText }) => {
  const searchParams = useSearchParams();

  const [filters, setFilters] = useState<CscFiltersType>([]);
  const [selectedFilters, setSelectedFilters] = useState<Filters>([]);
  const [searchResults, setSearchResults] = useState<SearchResults | undefined>(
    undefined,
  );
  const [numberOfResults, setNumberOfResults] = useState(0);
  const [searchExpression, setSearchExpression] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [bannerInfo, setBannerInfo] = useState<GetSearchResultProduct | null>(
    null,
  );

  const searchTermQuery = searchParams.get("q");
  const categoriesQuery = searchParams.get("c");
  const sitesQuery = searchParams.get("s");
  const pageQuery = searchParams.get("page");

  const getIndexOfFiltersType = (
    type: "c" | "s",
    filters: Filters | CscFiltersType,
  ): number => {
    return filters.findIndex(({ name }) => name === type);
  };

  const getValueFromFilters = (
    type: "c" | "s",
    searchFilters: Filters,
  ): string[] => {
    const foundFilters =
      searchFilters?.find(({ name }) => name === type)?.paths || [];
    const result =
      (filters[getIndexOfFiltersType(type, filters)]?.categories
        ?.filter((category) => foundFilters?.includes(category.name))
        ?.map((category) => category.value) as string[]) || [];

    return result;
  };

  const getValueFromQuery = useCallback(
    (
      type: "c" | "s",
      searchFilters: string[],
      filters: CscFiltersType,
    ): string[] => {
      const result =
        (filters[getIndexOfFiltersType(type, filters)]?.categories
          ?.filter((category) => searchFilters?.includes(category.value || ""))
          ?.map((category) => category.name) as string[]) || [];

      return result;
    },
    [],
  );

  const updateFilters = useCallback(
    (
      sitesFacets: GetSearchResultFilteredCategory[],
      categoryFacets: GetSearchResultFilteredCategory[],
      sitesSelected?: string[],
      categoriesSelected?: string[],
    ) => {
      return [
        sitesFacets &&
          sitesFacets.length > 0 && {
            label: sitesFilterText,
            name: "s",
            categories:
              sitesFacets
                ?.sort((a, b) => {
                  if (a.calculationPriority !== b.calculationPriority) {
                    return a.calculationPriority - b.calculationPriority;
                  }
                  return b.count - a.count;
                })
                ?.map((facet) => ({
                  type: "leaf",
                  name: `${facet.displayName} (${sitesSelected && !sitesSelected.includes(facet.categoryId) ? "+" : ""}${facet.count})`,
                  value: facet.categoryId,
                })) || [],
          },
        categoryFacets &&
          categoryFacets.length > 0 && {
            label: categoriesFilterText,
            name: "c",
            categories:
              categoryFacets
                ?.sort((a, b) => {
                  if (a.calculationPriority !== b.calculationPriority) {
                    return a.calculationPriority - b.calculationPriority;
                  }
                  return b.count - a.count;
                })
                ?.map((facet) => ({
                  type: "leaf",
                  name: `${facet.displayName} (${categoriesSelected && !categoriesSelected.includes(facet.categoryId) ? "+" : ""}${facet.count})`,
                  value: facet.categoryId,
                })) || [],
          },
      ].filter(Boolean) as {
        label: string;
        name: string;
        categories: TreeNode[];
      }[];
    },
    [categoriesFilterText, sitesFilterText],
  );

  const fetchSearchResults = useCallback(
    async (
      searchTerm: string,
      pageNumber: number,
      categoryFilter?: string[],
      siteFilter?: string[],
    ) => {
      try {
        setIsLoading(true);

        const response = await getCscSearchResults({
          searchTerm,
          categoryFilter: categoryFilter ? categoryFilter : [],
          siteFilter: siteFilter ? siteFilter : [],
          pageNumber: pageNumber,
        });

        setSearchResults(
          response ? ({ ...response } as SearchResults) : undefined,
        );
        setNumberOfResults(response?.totalHits || 0);
        setBannerInfo(response?.bannerProduct || null);

        if (pageNumber !== currentPage) {
          setCurrentPage((response?.nextPage || 2) - 1);
        }

        const allSitesFacets = response?.allSitesFacets || [];
        const allCategoryFacets = response?.allCategoryFacets || [];
        const newFilters = updateFilters(
          allSitesFacets,
          allCategoryFacets,
          siteFilter,
          categoryFilter,
        );

        setFilters(newFilters);
        setSelectedFilters([
          {
            name: "s",
            paths: getValueFromQuery("s", siteFilter || [], newFilters),
          },
          {
            name: "c",
            paths: getValueFromQuery("c", categoryFilter || [], newFilters),
          },
        ]);
        setIsLoading(false);
      } catch (error) {
        console.error(
          "[fetchSearchResults] Error fetching search results:",
          error,
        );
        setSearchResults(undefined);
        setFilters([]);
        setSelectedFilters([]);
        setNumberOfResults(0);
        setCurrentPage(1);
        setIsLoading(false);
        setBannerInfo(null);
      }
    },
    [currentPage, getValueFromQuery, updateFilters],
  );

  // Handle NEW search term (first load OR term change)
  useEffect(() => {
    if (!searchTermQuery) {
      if (searchExpression) {
        // if no search term clear all to initial state and return because it was cleared with previous value
        setFilters([]);
        setSelectedFilters([]);
        setSearchResults(undefined);
        setNumberOfResults(0);
        setSearchExpression("");
        setCurrentPage(1);
        setBannerInfo(null);
      }
      return;
    }

    const isNewSearch = searchTermQuery !== searchExpression;
    const isFirstLoad = !searchExpression;

    const page = pageQuery ? parseInt(pageQuery, 10) : 1;
    const c = categoriesQuery?.split(",") || [];
    const s = sitesQuery?.split(",") || [];

    if (isNewSearch) {
      setSearchExpression(searchTermQuery);

      if (isFirstLoad) {
        setCurrentPage(page);
      } else {
        // Search term changed - clear filters
        setCurrentPage(1);
        setSelectedFilters([]);
      }
    }
    fetchSearchResults(searchTermQuery, page, c, s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTermQuery, pageQuery, categoriesQuery, sitesQuery]);

  useEffect(() => {
    if (String(currentPage) !== pageQuery) {
      setQueryParameter("page", String(currentPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const onFilter = (newFilters: Filters) => {
    const c = getValueFromFilters("c", newFilters);
    const s = getValueFromFilters("s", newFilters);

    const setMap: Map<string, string> = new Map();
    const delMap: string[] = [];

    if (c.length > 0) {
      setMap.set("c", c.join(","));
    } else {
      delMap.push("c");
    }
    if (s.length > 0) {
      setMap.set("s", s.join(","));
    } else {
      delMap.push("s");
    }

    setMap.set("page", "1");
    setCurrentPage(1);

    updateQueryParameters({ set: setMap, delete: delMap });
  };

  const onGoToNextPage = () => {
    setCurrentPage((p) => p + 1);
  };

  const onGoToPreviousPage = () => {
    setCurrentPage((p) => p - 1);
  };

  return (
    <CscInternalSearchContext
      value={{
        searchResults,
        bannerInfo,
        filters,
        selectedFilters,
        isLoading,
        numberOfResults,
        searchExpression,
        currentPage,
        onFilter,
        onGoToNextPage,
        onGoToPreviousPage,
      }}
    >
      {children}
    </CscInternalSearchContext>
  );
};

export const CscInternalSearchContextBlock: FC<
  PropsWithChildren<CscInternalSearchContextBlockProps>
> = (props) => {
  return <Suspense>
    <CscInternalSearchContextBlockInner {...props} />
  </Suspense>
};